import { unwrapResult } from '@reduxjs/toolkit'
import { InputAdornment, Switch, makeStyles } from '@material-ui/core'
import { FastField, Formik, connect, getIn, useFormikContext } from 'formik'
import { merge, pick, pickBy } from 'lodash'
import moment from 'moment'
import PropTypes from 'prop-types'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useHistory } from 'react-router-dom'
import roundTo from 'round-to'
import * as Yup from 'yup'
import equal from 'fast-deep-equal'

import { useActionRunner } from 'src/modules/notification'
import {
  Button,
  Chip,
  Container,
  Form,
  FormikFileField,
  FormikTextField,
  FormikTimePicker,
  OrganisationName,
  PageTitle,
  ProductList,
  Typography,
  getFileParam,
  mapArrayErrorsToObject,
  FIELD_REQUIRED,
} from 'src/modules/ui'

import { useProducts } from '../../products'
import { useAdmin } from '../../admin'
import { useCustomers } from '../hooks'

const useStyles = makeStyles(theme => ({
  body: {
    backgroundColor: theme.palette.fadedPurple.main,
    borderRadius: theme.shape.borderRadius,
    display: 'flex',
    flexDirection: 'column',
    padding: theme.spacing(2),
    [theme.breakpoints.up('tablet')]: {
      flexDirection: 'row',
    },
  },
  chip: {
    marginLeft: theme.spacing(2),
  },
  content: {
    flexGrow: 1,
  },
  priceFields: {
    alignItems: 'center',
    display: 'flex',
  },
  priceField: {
    marginLeft: theme.spacing(1),
    width: 100,
  },
  selectAll: {
    alignItems: 'center',
    display: 'flex',
    marginBottom: theme.spacing(1),
    marginTop: theme.spacing(1),
  },
  sidePanelContainer: {
    flexShrink: 0,
    marginLeft: 0,
    marginBottom: theme.spacing(4),
    padding: theme.spacing(1),
    width: 'auto',
    [theme.breakpoints.up('tablet')]: {
      marginBottom: 0,
      marginLeft: theme.spacing(4),
      width: 250,
    },
  },
  submitButton: {
    marginTop: theme.spacing(1),
  },
}))

export const CustomerAgreement = ({
  allProducts,
  buyer,
  defaultDeliveryTime,
  defaultMinimumOrder,
  defaultOrderCutoff,
  defaultPaymentTerms,
  onSubmit,
  status,
  ...customerAgreement
}) => {
  const classes = useStyles()

  const reduceToProductObject = (acc, option) => ({
    ...acc,
    [option.id]: {
      included: '',
      discount: '',
      overridePrice: '',
    },
  })

  const initialValues = {
    discount: '',
    minimumOrder: defaultMinimumOrder || '',
    orderCutoff: defaultOrderCutoff
      ? moment(defaultOrderCutoff, 'HH:mm')
      : null,
    deliveryTime: defaultDeliveryTime || '',
    paymentTerms: defaultPaymentTerms || '',
    notes: '',
    document: null,
    agreementProducts: allProducts.reduce(reduceToProductObject, {}),
  }

  if (customerAgreement) {
    const {
      agreementProducts,
      orderCutoff,
      ...agreementRest
    } = customerAgreement
    const existing = pickBy(pick(agreementRest, Object.keys(initialValues)))

    agreementProducts.forEach(({ product, discount, overridePrice }) => {
      initialValues.agreementProducts[product.id] = {
        included: true,
        discount: discount || '',
        overridePrice: overridePrice || '',
      }
    })

    merge(initialValues, {
      orderCutoff: orderCutoff ? moment(orderCutoff, 'HH:mm') : undefined,
      ...existing,
    })
  }

  const reduceToProductValidation = (acc, option) => ({
    ...acc,
    [option.id]: Yup.object().shape({
      discount: Yup.number().typeError('Invalid number').nullable(),
      overridePrice: Yup.number()
        .typeError('Invalid number')
        .nullable()
        .when('discount', {
          is: val => !!val,
          then: Yup.number()
            .typeError('Invalid number')
            .test('only-1-override', 'Only discount or price', value => !value),
        }),
    }),
  })

  const validationSchema = Yup.object().shape({
    discount: Yup.number().typeError('Invalid number').nullable(),
    deliveryTime: Yup.number()
      .typeError('Invalid number')
      .nullable()
      .required(FIELD_REQUIRED),
    orderCutoff: Yup.date()
      .typeError('Invalid time')
      .nullable()
      .required(FIELD_REQUIRED),
    minimumOrder: Yup.number()
      .typeError('Invalid number')
      .nullable()
      .required(FIELD_REQUIRED),
    paymentTerms: Yup.number()
      .typeError('Invalid number')
      .nullable()
      .required(FIELD_REQUIRED),
    agreementProducts: Yup.object().shape(
      allProducts.reduce(reduceToProductValidation, {})
    ),
  })

  const handleSubmit = async (values, { setErrors }) => {
    const {
      agreementProducts,
      discount,
      document,
      orderCutoff,
      ...valuesRest
    } = values

    const params = {
      agreementProductItems: Object.keys(agreementProducts)
        .filter(id => agreementProducts[id].included)
        .map(id => ({
          productId: id,
          discount: agreementProducts[id].discount || undefined,
          overridePrice: agreementProducts[id].overridePrice || undefined,
        })),
      discount: discount || null,
      documentData: await getFileParam(document),
      orderCutoff: orderCutoff ? orderCutoff.format('HH:mm') : null,
      ...valuesRest,
    }

    const result = await onSubmit(params)
    try {
      unwrapResult(result)
    } catch (err) {
      if (err.data) {
        const { agreementProductItems, documentData, ...fieldErrors } = err.data

        if (agreementProductItems) {
          fieldErrors.agreementProducts = mapArrayErrorsToObject(
            agreementProductItems,
            params.agreementProductItems,
            'productId'
          )
        }

        setErrors({
          document: documentData,
          ...fieldErrors,
        })
      }
    }
  }

  return (
    <Container>
      <PageTitle>
        Customer Agreement
        {status === 'requested' ? (
          <Chip label="Requested" color="orange" className={classes.chip} />
        ) : status === 'proposed' ? (
          <Chip label="Proposed" color="darkGreen" className={classes.chip} />
        ) : status === 'accepted' ? (
          <Chip label="Accepted" color="lightGreen" className={classes.chip} />
        ) : null}
      </PageTitle>
      <OrganisationName
        {...buyer}
        subtitle={`Primary contact: ${buyer.primaryContactName} (${buyer.primaryContactEmail})`}
      />
      <Formik
        initialValues={initialValues}
        onSubmit={handleSubmit}
        validationSchema={validationSchema}
        validateOnBlur={false}
        validateOnChange={false}
      >
        {({ isSubmitting, handleSubmit, setFieldValue }) => (
          <InnerForm
            {...{
              allProducts,
              isSubmitting,
              handleSubmit,
              setFieldValue,
              status,
            }}
          />
        )}
      </Formik>
    </Container>
  )
}

const InnerForm = React.memo(
  ({ allProducts, isSubmitting, handleSubmit, setFieldValue, status }) => {
    const classes = useStyles()
    const [prodsVisible, setProdsVisible] = useState([])

    const ProductSwitchMemo = useMemo(
      () => props => <ProductSwitch {...props} setFieldValue={setFieldValue} />,
      []
    )

    const handleMatchingProductsChange = useCallback(products => {
      if (prodsVisible !== products) {
        setProdsVisible(products)
      }
    }, [])

    const selectComponent = useMemo(() => {
      const message =
        prodsVisible.length === allProducts.length
          ? `Include all ${allProducts.length.toLocaleString()} products`
          : `Include all ${prodsVisible.length.toLocaleString()} products from search`
      return (
        <div className={classes.selectAll}>
          <SelectAllProducts prodsVisible={prodsVisible} />
          <Typography>{message}</Typography>
        </div>
      )
    }, [allProducts, prodsVisible])

    return (
      <Form className={classes.body}>
        <div className={classes.content}>
          <ProductList
            onMatchingProductsChange={handleMatchingProductsChange}
            products={allProducts}
            productPreCell={props => <ProductSwitchMemo {...props} />}
            productPostCell={ProductPrice}
            showDescription={false}
            showPhoto={false}
            showSearch
            categoryColor="purple"
            subcategoryColor="purple"
            toolbarPreFilterComponent={() => selectComponent}
          />
        </div>
        <div className={classes.sidePanelContainer}>
          <Form.InputGroup title="Discount">
            <FormikTextField
              fullWidth
              InputProps={{
                endAdornment: (
                  <InputAdornment position="start">%</InputAdornment>
                ),
              }}
              InputLabelProps={{ shrink: true }}
              label="Discount all products"
              margin="normal"
              multiline
              name="discount"
              placeholder="0.00"
            />
          </Form.InputGroup>
          <Form.InputGroup title="Agreement Terms">
            <FormikTextField
              name="minimumOrder"
              margin="normal"
              label="Minimum Order"
              fullWidth
              placeholder="0.00"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">£</InputAdornment>
                ),
              }}
            />
            <FormikTimePicker
              InputLabelProps={{ shrink: true }}
              name="orderCutoff"
              margin="normal"
              label="Order cutoff time"
              placeholder="HH:MM"
              fullWidth
            />
            <FormikTextField
              name="deliveryTime"
              margin="normal"
              label="Delivery Time"
              fullWidth
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">within</InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="start">day(s)</InputAdornment>
                ),
              }}
            />
            <FormikTextField
              name="paymentTerms"
              margin="normal"
              label="Payment Terms"
              fullWidth
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">within</InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="start">day(s)</InputAdornment>
                ),
              }}
            />
            <FormikTextField
              fullWidth
              InputLabelProps={{ shrink: true }}
              label="Notes"
              margin="normal"
              multiline
              name="notes"
              placeholder="Type any notes..."
            />
            <FormikFileField
              fullWidth
              label="Document"
              margin="normal"
              name="document"
            />
          </Form.InputGroup>
          <Button
            className={classes.submitButton}
            color="primary"
            fullWidth
            isLoading={isSubmitting}
            onClick={handleSubmit}
            size="large"
          >
            {status === 'requested'
              ? 'Submit Proposal'
              : status === 'proposed'
              ? 'Update Proposal'
              : status === 'accepted'
              ? 'Update Agreement'
              : null}
          </Button>
        </div>
      </Form>
    )
  },
  equal
)

const ProductSwitch = ({ product, setFieldValue }) => {
  const { id } = product
  const name = `agreementProducts.${id}.included`

  return (
    <FastField name={name}>
      {props => (
        <ProductSwitchField {...props} id={id} setFieldValue={setFieldValue} />
      )}
    </FastField>
  )
}

const ProductSwitchField = ({ id, field, form, meta, setFieldValue }) => {
  const { name, value } = field
  const [lastValue, setLastValue] = useState(value)
  useEffect(() => {
    if (value !== lastValue) {
      if (!value) {
        setFieldValue(`agreementProducts.${id}.discount`, '')
        setFieldValue(`agreementProducts.${id}.overridePrice`, '')
      }
      setLastValue(value)
    }
  }, [value, lastValue, id, setFieldValue])

  return <Switch {...field} name={name} checked={Boolean(value)} />
}

class ProductPriceProviderInner extends React.Component {
  shouldComponentUpdate(props) {
    const { id } = props
    const discount = 'discount'
    const fieldIncluded = `agreementProducts.${id}.included`
    const fieldDiscount = `agreementProducts.${id}.discount`

    const oldProps = this.props
    const fieldChanged = name => {
      return (
        getIn(props.formik.values, name) !== getIn(oldProps.formik.values, name)
      )
    }

    const fields = [discount, fieldIncluded, fieldDiscount]
    return fields.some(fieldChanged)
  }

  render() {
    const { id } = this.props
    const discountName = 'discount'
    const fieldIncludedName = `agreementProducts.${id}.included`
    const fieldDiscountName = `agreementProducts.${id}.discount`

    const discount = getIn(this.props.formik.values, discountName)
    const productIncluded = getIn(this.props.formik.values, fieldIncludedName)
    const productDiscount = getIn(this.props.formik.values, fieldDiscountName)

    return this.props.children({
      discount,
      productIncluded,
      productDiscount,
      ...this.props,
    })
  }
}

const ProductPriceProvider = connect(ProductPriceProviderInner)

const ProductPrice = ({ product, values }) => {
  const classes = useStyles()
  const { variablePrice, variablePriceUnit } = product

  return (
    <ProductPriceProvider id={product.id}>
      {({ discount, productIncluded, productDiscount }) => {
        const { id, price } = product

        if (!productIncluded) {
          return null
        }

        let currentPrice = Number(price)
        if (productDiscount) {
          currentPrice = currentPrice * ((100 - productDiscount) / 100)
        } else if (discount) {
          currentPrice = currentPrice * ((100 - discount) / 100)
        }
        currentPrice = String(roundTo(currentPrice, 2))

        return (
          <div className={classes.priceFields}>
            <FormikTextField
              className={classes.priceField}
              label="Discount"
              name={`agreementProducts.${id}.discount`}
              placeholder="0.00"
              InputLabelProps={{ shrink: true }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="start">%</InputAdornment>
                ),
              }}
            />
            <FormikTextField
              className={classes.priceField}
              label={variablePrice ? `Price per ${variablePriceUnit}` : 'Price'}
              name={`agreementProducts.${id}.overridePrice`}
              placeholder={currentPrice}
              InputLabelProps={{ shrink: true }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">£</InputAdornment>
                ),
              }}
            />
          </div>
        )
      }}
    </ProductPriceProvider>
  )
}

const SelectAllProducts = ({ prodsVisible }) => {
  const { setValues, values } = useFormikContext()
  const { agreementProducts, ...otherValues } = values

  const visibleIds = prodsVisible.map(p => String(p.id))
  const visibleAgreementProducts = pickBy(agreementProducts, (value, key) =>
    visibleIds.includes(key)
  )

  const allChecked = Object.values(visibleAgreementProducts).every(
    ({ included }) => included === true
  )

  const handleChange = event => {
    const { checked } = event.target
    const updatedValues = Object.keys(visibleAgreementProducts).reduce(
      (values, id) => ({
        ...values,
        [id]: {
          ...visibleAgreementProducts[id],
          included: checked,
        },
      }),
      {}
    )
    setValues({
      ...otherValues,
      agreementProducts: {
        ...agreementProducts,
        ...updatedValues,
      },
    })
  }

  return <Switch checked={allChecked} onChange={handleChange} />
}

CustomerAgreement.propTypes = {
  allProducts: PropTypes.array,
  defaultDeliveryTime: PropTypes.number,
  defaultMinimumOrder: PropTypes.string,
  defaultOrderCutoff: PropTypes.string,
  defaultPaymentTerms: PropTypes.number,
  deliveryTime: PropTypes.number,
  discount: PropTypes.string,
  document: PropTypes.string,
  minimumOrder: PropTypes.string,
  notes: PropTypes.string,
  onSubmit: PropTypes.func,
  orderCutoff: PropTypes.string,
  paymentTerms: PropTypes.number,
  status: PropTypes.oneOf(['requested', 'proposed', 'accepted']),
}

const CustomerAgreementContainer = ({ match }) => {
  const { id } = match.params

  const history = useHistory()
  const {
    customerAgreement,
    fetchSingleCustomerAgreement,
    updateCustomerAgreement,
  } = useCustomers()
  const { fetchProducts, products } = useProducts()
  const { fetchProfile, profile } = useAdmin()

  const [doFetchData, isFetching] = useActionRunner(
    [() => fetchSingleCustomerAgreement(id), fetchProducts, fetchProfile],
    {
      initialFetchingState: true,
    }
  )

  const [doUpdateCustomerAgreement] = useActionRunner(updateCustomerAgreement, {
    successNotification: 'Updated customer agreement',
  })

  const handleSubmit = async values => {
    const result = await doUpdateCustomerAgreement({
      id,
      params: values,
    })
    try {
      const { status } = unwrapResult(result)
      if (status === 'accepted') {
        history.push('/customers/approved')
      } else {
        history.push('/customers/proposals')
      }
    } catch (err) {}
    return result
  }

  useEffect(() => {
    doFetchData()
  }, [])

  if (isFetching) {
    return null
  }

  const defaultFields = [
    'defaultDeliveryTime',
    'defaultMinimumOrder',
    'defaultOrderCutoff',
    'defaultPaymentTerms',
  ]
  const defaults = pick(profile, defaultFields)

  return (
    <CustomerAgreement
      allProducts={products}
      onSubmit={handleSubmit}
      {...defaults}
      {...customerAgreement}
    />
  )
}

export default CustomerAgreementContainer
